import '@purple-dot/design-system/global-style.css';
import PurpleDotThemeProvider from '@purple-dot/design-system/theme';
import { ExtraErrorData } from '@sentry/integrations';
import * as Sentry from '@sentry/react';
import App, { AppContext, AppInitialProps, AppProps } from 'next/app';
import { NextIncomingMessage } from 'next/dist/server/request-meta';
import React from 'react';
import { ConfigContext } from '../components/common/config-context';
import '../public/nprogress.css';
import '../public/reboot.css';

interface Config {
  host: string;
  googleClientId: string;
  sentryDSN?: string;
  appEnv: string;
  appRelease?: string;
  appName: string;
}

type AppOwnProps = {
  configProps?: Config;
};

function PurpleDotApp({
  Component,
  pageProps,
  configProps,
}: AppProps & AppOwnProps) {
  if (configProps?.sentryDSN) {
    Sentry.init({
      dsn: configProps.sentryDSN,
      environment: configProps.appEnv,
      release: configProps.appRelease,
      autoSessionTracking: false,
      integrations: [
        new ExtraErrorData({
          depth: 10,
        }),
      ],
      denyUrls: [
        // Chrome extensions
        /extensions\//i,
        /^chrome:\/\//i,
        /^chrome-extension:\/\//i,
      ],
    });
  }

  return (
    <Sentry.ErrorBoundary>
      <ConfigContext.Provider
        value={{
          googleClientId: configProps?.googleClientId,
          host: configProps?.host,
        }}
      >
        <PurpleDotThemeProvider>
          <Component {...pageProps} />
        </PurpleDotThemeProvider>
      </ConfigContext.Provider>
    </Sentry.ErrorBoundary>
  );
}

PurpleDotApp.getInitialProps = async (
  context: AppContext
): Promise<AppInitialProps & AppOwnProps> => {
  const ctx = await App.getInitialProps(context);

  if (context.ctx.req) {
    const { clientSideConfig } = context.ctx.req as NextIncomingMessage & {
      clientSideConfig: Config;
    };

    return {
      ...ctx,
      configProps: clientSideConfig,
    };
  }

  return { ...ctx };
};

export default PurpleDotApp;
